import { DateTime, IANAZone } from 'luxon';

const assertValidTimeZone = (timeZone: string): void => {
  if (!IANAZone.isValidZone(timeZone)) {
    throw new RangeError(`Invalid time zone: ${timeZone}`);
  }
};

// Interpreta como UTC las fechas del backend, incluso si la cadena no incluye zona.
const parseUtcInstant = (value: string | Date): DateTime => {
  const dateTime =
    value instanceof Date
      ? DateTime.fromJSDate(value, { zone: 'UTC' })
      : DateTime.fromISO(value, { zone: 'UTC' });

  if (!dateTime.isValid) {
    throw new RangeError(`Invalid UTC date-time: ${String(value)}`);
  }

  return dateTime.toUTC();
};

export const formatUtcTimestamp = (
  value: string | Date,
  timeZone: string,
  locale = 'es-ES',
  options: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
  }
): string => {
  assertValidTimeZone(timeZone);
  return parseUtcInstant(value)
    .setZone(timeZone)
    .setLocale(locale)
    .toLocaleString(options);
};

/**
 * Converts the wall-clock fields of a date-time picker value from the selected
 * zone to UTC. Throws for daylight-saving gaps and repeated local times.
 */
export const localDateTimeToUtc = (value: Date, timeZone: string): string => {
  assertValidTimeZone(timeZone);

  if (Number.isNaN(value.getTime())) {
    throw new RangeError('Invalid local date-time');
  }

  const parts = {
    year: value.getFullYear(),
    month: value.getMonth() + 1,
    day: value.getDate(),
    hour: value.getHours(),
    minute: value.getMinutes(),
    second: value.getSeconds(),
    millisecond: value.getMilliseconds(),
  };
  const dateTime = DateTime.fromObject(parts, { zone: timeZone });

  if (!dateTime.isValid) {
    throw new RangeError(
      `Invalid local date-time in ${timeZone}: ${dateTime.invalidReason}`
    );
  }

  const matchesRequestedTime =
    dateTime.year === parts.year &&
    dateTime.month === parts.month &&
    dateTime.day === parts.day &&
    dateTime.hour === parts.hour &&
    dateTime.minute === parts.minute &&
    dateTime.second === parts.second &&
    dateTime.millisecond === parts.millisecond;

  if (!matchesRequestedTime) {
    throw new RangeError(
      `Local date-time does not exist in ${timeZone} because of a daylight-saving transition`
    );
  }

  if (dateTime.getPossibleOffsets().length > 1) {
    throw new RangeError(
      `Local date-time is ambiguous in ${timeZone} because of a daylight-saving transition`
    );
  }

  return dateTime.toUTC().toISO();
};

// Convierte un día seleccionado a límites UTC; el final exclusivo también funciona
// en días de 23 o 25 horas por el cambio de horario.
export const getUtcDayRange = (
  value: Date,
  timeZone: string
): { start: Date; endExclusive: Date } => {
  assertValidTimeZone(timeZone);

  if (Number.isNaN(value.getTime())) {
    throw new RangeError('Invalid calendar date');
  }

  const dateTime = DateTime.fromObject(
    {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
    },
    { zone: timeZone }
  ).startOf('day');

  if (
    !dateTime.isValid ||
    dateTime.year !== value.getFullYear() ||
    dateTime.month !== value.getMonth() + 1 ||
    dateTime.day !== value.getDate()
  ) {
    throw new RangeError(`Calendar date does not exist in ${timeZone}`);
  }

  const nextDay = dateTime.plus({ days: 1 }).startOf('day');

  return {
    start: dateTime.toUTC().toJSDate(),
    endExclusive: nextDay.toUTC().toJSDate(),
  };
};

// Aplica la misma conversión cuando el día seleccionado procede de una URL.
export const getUtcDayRangeForIsoDate = (
  value: string,
  timeZone: string
): { start: Date; endExclusive: Date } => {
  assertValidTimeZone(timeZone);
  const dateTime = DateTime.fromISO(value, { zone: timeZone }).startOf('day');

  if (!dateTime.isValid || dateTime.toISODate() !== value) {
    throw new RangeError(`Invalid calendar date: ${value}`);
  }

  return {
    start: dateTime.toUTC().toJSDate(),
    endExclusive: dateTime.plus({ days: 1 }).startOf('day').toUTC().toJSDate(),
  };
};

// Crea una fecha para el selector usando el día de calendario de la zona elegida.
export const getCalendarDateInTimeZone = (
  value: string | Date,
  timeZone: string
): Date => {
  assertValidTimeZone(timeZone);
  const dateTime = parseUtcInstant(value).setZone(timeZone);
  return new Date(dateTime.year, dateTime.month - 1, dateTime.day);
};

// Obtiene los campos de hora local del selector a partir de un instante UTC.
export const getLocalDateTimeInTimeZone = (
  value: string | Date,
  timeZone: string
): Date => {
  assertValidTimeZone(timeZone);
  const dateTime = parseUtcInstant(value).setZone(timeZone);
  return new Date(
    dateTime.year,
    dateTime.month - 1,
    dateTime.day,
    dateTime.hour,
    dateTime.minute,
    dateTime.second,
    dateTime.millisecond
  );
};

export const getUtcDateKeyInTimeZone = (
  value: string | Date,
  timeZone: string
): string => {
  assertValidTimeZone(timeZone);
  const dateTime = parseUtcInstant(value).setZone(timeZone);
  return dateTime.toFormat('yyyy-MM-dd');
};
